import DOMPurify from 'dompurify';

/**
 * QA-335 — BO 에디터 미리보기를 FE(storefront) 렌더와 같은 규칙으로 그린다.
 * storefront src/lib/sanitizeHtml.ts 와 같은 정책이다.
 *   - script·이벤트 핸들러·javascript: 제거, <style> 요소 차단
 *   - 인라인 style 은 표현 속성(색·배경·글꼴·굵기·크기·줄간격·자간·밑줄)만 통과
 *   - 정렬·여백·테두리·폭 같은 레이아웃 속성은 빠진다(ID-152)
 * 그래서 BO 미리보기에서 빠진 서식은 FE 에서도 빠진다. 규칙이 바뀌면 storefront 쪽과 함께 맞춘다.
 *
 * 앱의 다른 DOMPurify 사용처(이메일 미리보기 등)에 훅이 새지 않도록 전용 인스턴스를 쓴다.
 */
const SAFE_STYLE_PROPS = new Set<string>([
  'color',
  'background-color',
  'background',
  'font-weight',
  'font-style',
  'font-size',
  'font-family',
  'text-decoration',
  'text-decoration-line',
  'text-decoration-color',
  'text-decoration-style',
  'line-height',
  'letter-spacing',
]);

const hasUnsafeCssValue = (value: string): boolean => {
  const v = value.toLowerCase();
  return (
    v.includes('url(') ||
    v.includes('expression') ||
    v.includes('javascript:') ||
    v.includes('@import') ||
    v.includes('/*') ||
    v.includes('*/') ||
    v.includes('<') ||
    v.includes('>') ||
    v.includes('\\')
  );
};

export const sanitizeStyleValue = (style: string): string => {
  const safe: string[] = [];
  for (const decl of style.split(';')) {
    const idx = decl.indexOf(':');
    if (idx < 0) continue;
    const prop = decl.slice(0, idx).trim().toLowerCase();
    const value = decl.slice(idx + 1).trim();
    if (!prop || !value) continue;
    if (!SAFE_STYLE_PROPS.has(prop)) continue;
    if (hasUnsafeCssValue(value)) continue;
    safe.push(`${prop}: ${value}`);
  }
  return safe.join('; ');
};

let purify: ReturnType<typeof DOMPurify> | null = null;
const instance = (): ReturnType<typeof DOMPurify> => {
  if (purify) return purify;
  purify = DOMPurify(window);
  purify.addHook('uponSanitizeAttribute', (_node, data) => {
    if (data.attrName !== 'style') return;
    const cleaned = sanitizeStyleValue(data.attrValue ?? '');
    if (cleaned.length === 0) {
      data.keepAttr = false;
      return;
    }
    data.attrValue = cleaned;
  });
  return purify;
};

/** FE 와 같은 규칙으로 정리한 HTML(미리보기용). */
export const sanitizeLikeStorefront = (raw: string | null | undefined): string => {
  if (!raw) return '';
  return instance().sanitize(raw, {
    ADD_ATTR: ['target', 'rel', 'style'],
    FORBID_TAGS: ['style'],
  });
};

